import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Trash2, Edit, Save, X, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [notification, setNotification] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: ''
  });

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging-config/templates`, { headers });
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      setNotification({ type: 'error', message: 'Tous les champs sont obligatoires' });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/messaging-config/templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setNotification({ type: 'success', message: 'Template créé' });
        loadTemplates();
        setShowAdd(false);
        setFormData({ name: '', subject: '', body: '' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la création' });
    }
  };

  const handleUpdate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging-config/templates/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setNotification({ type: 'success', message: 'Template modifié' });
        loadTemplates();
        setEditingId(null);
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la modification' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/messaging-config/templates/${id}`, {
        method: 'DELETE',
        headers
      });
      
      if (res.ok) {
        setNotification({ type: 'success', message: 'Template supprimé' });
        loadTemplates();
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  };

  const startEdit = (template) => {
    setEditingId(template.id);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body
    });
    setExpandedId(template.id);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Templates Email</h3>
            <p className="text-sm text-gray-500">Gérer les modèles d'emails</p>
          </div>
        </div>
        
        <button
          onClick={() => { setShowAdd(!showAdd); setEditingId(null); setFormData({ name: '', subject: '', body: '' }); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
        >
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 ${
          notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {notification.message}
        </div>
      )}

      {/* Info box */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Variables disponibles :</p>
            <code className="text-xs bg-blue-100 px-1 rounded">{'{bloc_identification}'}</code> - Infos du client<br />
            <code className="text-xs bg-blue-100 px-1 rounded">{'{client_nom}'}</code> - Nom du client<br />
            <code className="text-xs bg-blue-100 px-1 rounded">{'{client_prenom}'}</code> - Prénom du client<br />
            <code className="text-xs bg-blue-100 px-1 rounded">{'{gestionnaire_nom}'}</code> - Nom du gestionnaire<br />
            <code className="text-xs bg-blue-100 px-1 rounded">{'{date_envoi}'}</code> - Date du jour
          </div>
        </div>
      </div>

      {/* Formulaire d'ajout */}
      {showAdd && (
        <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <h4 className="font-semibold text-emerald-800 mb-4">Nouveau template</h4>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Nom du template"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
            <input
              type="text"
              placeholder="Objet de l'email"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
            <textarea
              placeholder="Corps du message..."
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              rows="8"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des templates */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Chargement...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">Aucun template</div>
      ) : (
        <div className="space-y-2">
          {templates.map(template => (
            <div key={template.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => setExpandedId(expandedId === template.id ? null : template.id)}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <span className="font-medium">{template.name}</span>
                  {template.is_default && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Par défaut</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(template); }}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(template.id); }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedId === template.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {expandedId === template.id && (
                <div className="p-4 border-t border-gray-200">
                  {editingId === template.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Nom"
                      />
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Objet"
                      />
                      <textarea
                        value={formData.body}
                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                        rows="8"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUpdate(template.id)}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">
                        <strong>Objet :</strong> {template.subject}
                      </p>
                      <pre className="text-sm bg-gray-50 p-3 rounded-lg whitespace-pre-wrap font-mono">
                        {template.body}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WidgetEmailTemplates;
